"use client";

import React from "react";
import { CircleCheck as CheckCircle2, Loader as Loader2, Building2, CircleAlert as AlertCircle } from 'lucide-react';
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function SupplierFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <Card className="border-[var(--color-border)] shadow-md text-center py-12 animate-fade show">
        <CardContent className="flex flex-col items-center justify-center space-y-6">
          <div className="size-16 rounded-lg bg-[var(--color-accent)]/20 flex items-center justify-center mb-4">
            <CheckCircle2 className="size-8 text-[var(--color-primary)]" />
          </div>
          <CardTitle className="text-2xl font-default text-[var(--color-primary)]">
            Registration Successful
          </CardTitle>
          <CardDescription className="text-base max-w-md mx-auto text-[var(--color-text-dark)]/80">
            {successMessage}
          </CardDescription>
          <Button onClick={resetForm} className="mt-8 bg-[var(--color-primary)] text-[var(--color-text-light)] hover:bg-[var(--color-primary)]/90">
            Register Another Supplier
          </Button>
        </CardContent>
      </Card>;
  }
  return <Card className="border-[var(--color-border)] shadow-md">
      <CardHeader className="bg-[var(--color-primary)]/5 border-b border-[var(--color-border)] pb-6">
        <div className="flex items-center gap-3 mb-2">
          <Building2 className="size-6 text-[var(--color-accent)]" />
          <CardTitle className="text-xl font-default text-[var(--color-primary)]">
            Supplier Details
          </CardTitle>
        </div>
        <CardDescription className="text-[var(--color-text-dark)]/70">
          Please provide your company and operational information to join our network.
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-8">
        <Form onSubmit={handleSubmit}>
          <div className="space-y-8">
            {}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <FormField control={control} name="companyName" rules={{
                required: "Company Name is required",
                minLength: {
                  value: 2,
                  message: "Must be at least 2 characters"
                }
              }} render={({
                field
              }) => <FormItem>
                      <FormLabel className="text-[var(--color-text-dark)]">Company Name</FormLabel>
                      <FormControl>
                        <Input placeholder="e.g. Global Trade LLC" className="border-[var(--color-border)] focus-visible:ring-[var(--color-accent)]" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>} />
              </div>
              <FormField control={control} name="contactPerson" rules={{
              required: "Contact Person is required"
            }} render={({
              field
            }) => <FormItem>
                    <FormLabel className="text-[var(--color-text-dark)]">Contact Person</FormLabel>
                    <FormControl>
                      <Input placeholder="Full Name" className="border-[var(--color-border)] focus-visible:ring-[var(--color-accent)]" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />
              <FormField control={control} name="country" rules={{
              required: "Country is required"
            }} render={({
              field
            }) => <FormItem>
                    <FormLabel className="text-[var(--color-text-dark)]">Country</FormLabel>
                    <FormControl>
                      <Input placeholder="e.g. United Arab Emirates" className="border-[var(--color-border)] focus-visible:ring-[var(--color-accent)]" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />
            </div>

            <div className="h-px w-full bg-[var(--color-border)]" />

            {}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FormField control={control} name="brands" rules={{
              required: "Brands information is required"
            }} render={({
              field
            }) => <FormItem>
                    <FormLabel className="text-[var(--color-text-dark)]">Brands Handled</FormLabel>
                    <FormControl>
                      <Input placeholder="e.g. Samsung, Apple, Sony" className="border-[var(--color-border)] focus-visible:ring-[var(--color-accent)]" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />
              <FormField control={control} name="stock" rules={{
              required: "Stock capacity is required"
            }} render={({
              field
            }) => <FormItem>
                    <FormLabel className="text-[var(--color-text-dark)]">Stock Capacity / Volume</FormLabel>
                    <FormControl>
                      <Input placeholder="e.g. 10,000+ units monthly" className="border-[var(--color-border)] focus-visible:ring-[var(--color-accent)]" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />
              <FormField control={control} name="email" rules={{
              required: "Email is required",
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: "Please enter a valid email address"
              }
            }} render={({
              field
            }) => <FormItem>
                    <FormLabel className="text-[var(--color-text-dark)]">Business Email</FormLabel>
                    <FormControl>
                      <Input type="email" placeholder="contact@company.com" className="border-[var(--color-border)] focus-visible:ring-[var(--color-accent)]" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />
              <FormField control={control} name="whatsapp" rules={{
              required: "WhatsApp number is required"
            }} render={({
              field
            }) => <FormItem>
                    <FormLabel className="text-[var(--color-text-dark)]">WhatsApp Number</FormLabel>
                    <FormControl>
                      <Input type="tel" placeholder="+1 (555) 000-0000" className="border-[var(--color-border)] focus-visible:ring-[var(--color-accent)]" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />
            </div>

            {submitError && <Alert variant="destructive" className="bg-red-50 border-red-200 text-red-800">
                <AlertCircle className="size-4" />
                <AlertTitle>Submission Failed</AlertTitle>
                <AlertDescription>{submitError}</AlertDescription>
              </Alert>}

            <div className="pt-4">
              <Button type="submit" disabled={isSubmitting} className="w-full md:w-auto md:min-w-[200px] bg-[var(--color-primary)] text-[var(--color-text-light)] hover:bg-[var(--color-primary)]/90 focus-visible:ring-[var(--color-accent)] h-12 text-base">
                {isSubmitting ? <>
                    <Loader2 className="animate-spin mr-2 size-5" />
                    Submitting Registration...
                  </> : "Submit Registration"}
              </Button>
            </div>
          </div>
        </Form>
      </CardContent>
    </Card>;
}
export default function SupplierRegistrationForm() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="supplierregistrationform" className="bg-background py-16 md:py-24 overflow-hidden">
      <div className="container mx-auto px-4 md:px-6 max-w-4xl">
        <div ref={ref} className={`transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold font-default text-[var(--color-primary)] mb-4">
              Become a Supplier
            </h2>
            <p className="text-lg text-[var(--color-text-dark)]/80 max-w-2xl mx-auto">
              Join our global network of trusted suppliers. Fill out the form below to start the onboarding process and expand your reach.
            </p>
          </div>

          <FormProvider formId="supplier-registration-main" sectionName="Supplier Registration" defaultValues={{
          companyName: "",
          contactPerson: "",
          country: "",
          brands: "",
          stock: "",
          email: "",
          whatsapp: ""
        }} submitText="Submit Registration" successMessage="Thank you for registering! Our team will review your application and contact you shortly.">
            <SupplierFormContent />
          </FormProvider>
        </div>
      </div>
    </section>;
}